import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PeriodInput, Loading } from "@/components/common";
import { CustStatusBadge } from "@/components/badges";
import { api, errMsg } from "@/lib/api";
import { currentPeriod, num, rupiah, supplyLabelOf, todayLocal } from "@/lib/format";

function SupplyForm({ customer, period, onDone }) {
  const [types, setTypes] = useState(null);
  const [qty, setQty] = useState({});
  const [f, setF] = useState({ date: todayLocal(), ref: "", note: "" });
  useEffect(() => {
    setTypes(null);
    Promise.all([api.get("/prices"), api.get("/supplies", { params: { period } })]).then(([v, s]) => {
      const old = s.data.find((x) => x.customer_id === customer.id);
      setQty(Object.fromEntries((old?.lines || []).map((l) => [l.voucher_type_id, l.qty])));
      setF(old ? { date: old.date, ref: old.ref, note: old.note || "" } : { date: todayLocal(), ref: "", note: "" });
      setTypes(v.data.filter((x) => x.active));
    }).catch((e) => toast.error(errMsg(e)));
  }, [customer.id, period]);
  if (!types) return <Loading />;
  const total = types.reduce((s, v) => s + (qty[v.id] || 0), 0);
  const value = types.reduce((s, v) => s + (qty[v.id] || 0) * v.price, 0);
  const save = async () => {
    try {
      const { data } = await api.put("/supplies", { period, customer_id: customer.id, ...f, lines: types.map((v) => ({ voucher_type_id: v.id, qty: qty[v.id] || 0 })) });
      toast.success(data.deleted ? "Pasokan awal dihapus" : `Pasokan awal ${data.ref} disimpan`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="xl-wrap max-h-[40vh]">
        <table className="xl">
          <thead><tr><th>Jenis Voucher</th><th className="text-right">Harga</th><th className="text-right">Qty Pasokan Awal</th><th className="text-right">Nilai</th></tr></thead>
          <tbody>
            {types.map((v, i) => (
              <tr key={v.id}>
                <td>{v.name}</td><td className="num">{num(v.price)}</td>
                <td className="cell-edit"><input type="number" min={0} className="xl-input" value={qty[v.id] || ""} placeholder="0" onChange={(e) => setQty((s) => ({ ...s, [v.id]: Math.max(0, parseInt(e.target.value, 10) || 0) }))} data-testid={`supply-qty-${i}`} /></td>
                <td className="num">{num((qty[v.id] || 0) * v.price)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={2}>TOTAL</td><td className="num" data-testid="supply-total-qty">{num(total)}</td><td className="num">{num(value)}</td></tr></tfoot>
        </table>
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} className="h-11" data-testid="supply-date-input" /></div>
        <div><Label>Referensi</Label><Input value={f.ref} placeholder="otomatis" onChange={(e) => setF((s) => ({ ...s, ref: e.target.value }))} className="h-11" data-testid="supply-ref-input" /></div>
        <div><Label>Catatan</Label><Input value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} className="h-11" data-testid="supply-note-input" /></div>
      </div>
      <p className="text-xs text-slate-500">Nilai pasokan awal ({rupiah(value)}) dicatat terpisah. Nilai ini tidak masuk penjualan aktual, kewajiban top up, atau hutang penjualan.</p>
      <DialogFooter><Button onClick={save} className="h-11 bg-violet-700 hover:bg-violet-800" data-testid="supply-save-btn">Simpan Pasokan Awal</Button></DialogFooter>
    </div>
  );
}

export function SupplyDialog({ customer, period, onClose, onDone }) {
  if (!customer) return null;
  return <SupplyDialogInner key={`${customer.id}-${period || ""}`} customer={customer} initialPeriod={period} onClose={onClose} onDone={onDone} />;
}

function SupplyDialogInner({ customer, initialPeriod, onClose, onDone }) {
  const [period, setPeriod] = useState(initialPeriod || currentPeriod());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="supply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">Input Pasokan Awal · {customer.name} <CustStatusBadge status={customer.status} /></DialogTitle>
          <DialogDescription>Jumlah diisi manual, <b>tidak ditambah 30%</b>, dan <b>tidak dihitung sebagai penjualan aktual</b>. Data penjualan lama tidak dipakai.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">Periode estimasi</span><PeriodInput value={period} onChange={setPeriod} testId="supply-period-input" />
          <span className="font-medium text-violet-800" data-testid="supply-period-label">{supplyLabelOf(period)}</span>
        </div>
        <SupplyForm key={period} customer={customer} period={period} onDone={() => { onDone?.(); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
}
